import { ensureInitialClass } from "./app";
import { mountAll } from "./ui/view";
import { el, qs } from "./utils/dom";
import { getState, setState } from "./state/store";
import { generateGroups } from "./logic/generator";
import { generateSeatingAssignment } from "./logic/seating";
import { runWithMinDuration, mountProgress } from "./ui/progress";
import { openRosterDialog } from "./ui/components/RosterDialog";
import { openSettingsDialog } from "./ui/components/SettingsDialog";
import { errorToast, infoToast, toast } from "./ui/components/Toasts";
import { openPresentationMode } from "./ui/components/PresentationMode";
import { openPrintDialog } from "./ui/components/PrintDialog";
import { undo, redo, canUndo, canRedo } from "./state/history";
import { undoSeating, redoSeating, canUndoSeating, canRedoSeating, pushSeatingHistory } from "./state/seatingHistory";
import { getClientId, getClientName, setClientName } from "./state/client";
import { nid } from "./utils/id";
import type { ProximityConstraint, ProximityType, ThemePreference } from "./types/models";
import { openShortcutsPanel, setupShortcutsPanelKeyboard } from "./ui/components/ShortcutsPanel";
import { downloadGroupsCSV, downloadSeatingCSV, downloadRosterCSV, printGroupsAsPDF, printSeatingAsPDF } from "./utils/export";
import { checkAndShowOnboarding, showOnboardingWizard } from "./ui/components/OnboardingWizard";
import { openHistoryLogDialog } from "./ui/components/HistoryLogDialog";

// Application initialization
console.info("🚀 Group Generator Initializing");

ensureInitialClass();

console.info("📦 Mounting UI components");
mountAll();
console.info("✅ UI components mounted");

const btnRoster = qs<HTMLButtonElement>("#btn-roster");
const btnSettings = qs<HTMLButtonElement>("#btn-settings");
const btnPresent = qs<HTMLButtonElement>("#btn-present");
const btnPrint = qs<HTMLButtonElement>("#btn-print");
const undoBtn = qs<HTMLButtonElement>("#undo-btn");
const redoBtn = qs<HTMLButtonElement>("#redo-btn");
const btnGen = qs<HTMLButtonElement>("#btn-generate");
const btnToggleSidebar = qs<HTMLButtonElement>("#btn-toggle-sidebar");
const mainLayout = qs<HTMLElement>("#main-layout");
const clientNameInput = qs<HTMLInputElement>("#client-name-input");

// Initialize client name input
clientNameInput.value = getClientName();
clientNameInput.addEventListener("change", () => {
    setClientName(clientNameInput.value.trim());
    syncWithServer(); // Sync immediately when name changes
});
clientNameInput.addEventListener("blur", () => {
    setClientName(clientNameInput.value.trim());
});

// Initialize sidebar state from localStorage
const sidebarVisible = localStorage.getItem("GG:v1:sidebarVisible") !== "false";
if (!sidebarVisible) {
    mainLayout.classList.add("sidebar-hidden");
    btnToggleSidebar.textContent = "‣";
    btnToggleSidebar.title = "Show sidebar";
}

// Sidebar toggle handler
btnToggleSidebar.addEventListener("click", () => {
    const isHidden = mainLayout.classList.toggle("sidebar-hidden");

    if (isHidden) {
        btnToggleSidebar.textContent = "‣";
        btnToggleSidebar.title = "Show sidebar";
    } else {
        btnToggleSidebar.textContent = "☰";
        btnToggleSidebar.title = "Hide sidebar";
    }

    localStorage.setItem("GG:v1:sidebarVisible", String(!isHidden));
});

btnRoster.addEventListener("click", () => openRosterDialog());
btnSettings.addEventListener("click", () => openSettingsDialog());
btnPresent.addEventListener("click", () => openPresentationMode());
btnPrint.addEventListener("click", () => openPrintDialog());

// Theme toggle button
const btnTheme = qs<HTMLButtonElement>("#btn-theme");
const btnShortcuts = qs<HTMLButtonElement>("#btn-shortcuts");
const btnExport = qs<HTMLButtonElement>("#btn-export");

// Initialize theme from state or localStorage
function initTheme() {
    const s = getState();
    const theme = s.theme ?? (localStorage.getItem("GG:v1:theme") as ThemePreference) ?? "auto";
    applyTheme(theme);
    updateThemeButton(theme);
}

function applyTheme(theme: ThemePreference) {
    document.documentElement.classList.remove("theme-light", "theme-dark");
    if (theme === "light") {
        document.documentElement.classList.add("theme-light");
    } else if (theme === "dark") {
        document.documentElement.classList.add("theme-dark");
    }
    // 'auto' uses system preference (no class needed)
}

function updateThemeButton(theme: ThemePreference) {
    const icons: Record<ThemePreference, string> = {
        auto: "🌓",
        light: "☀️",
        dark: "🌙"
    };
    btnTheme.textContent = icons[theme];
    btnTheme.title = `Theme: ${theme} (click to change)`;
}

function cycleTheme() {
    const s = getState();
    const current = s.theme ?? "auto";
    const next: ThemePreference = current === "auto" ? "light" : current === "light" ? "dark" : "auto";

    setState(ns => { ns.theme = next; });
    localStorage.setItem("GG:v1:theme", next);
    applyTheme(next);
    updateThemeButton(next);
    toast(`Theme: ${next}`);
}

btnTheme.addEventListener("click", cycleTheme);
initTheme();

// Keyboard shortcuts button
btnShortcuts.addEventListener("click", () => openShortcutsPanel());
setupShortcutsPanelKeyboard();

// Export button - shows export menu
btnExport.addEventListener("click", () => {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) {
        errorToast("No class selected");
        return;
    }

    // Create export menu
    const backdrop = el("div", {
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    const menu = el("div", {
        style: "background: var(--card); border-radius: 12px; padding: 20px; min-width: 280px; border: 1px solid var(--border);"
    });

    const title = el("h3", { style: "margin: 0 0 16px 0;" }, "Export Data");

    const mode = getCurrentMode();

    const options = el("div", { style: "display: flex; flex-direction: column; gap: 8px;" });

    // Roster CSV
    const rosterBtn = el("button", { style: "width: 100%; text-align: left; padding: 12px;" }, "📋 Export Roster (CSV)");
    rosterBtn.addEventListener("click", () => {
        downloadRosterCSV(cls.students, `${cls.name}-roster.csv`);
        toast("Roster exported");
        backdrop.remove();
    });
    options.appendChild(rosterBtn);

    if (mode === "groups" && cls.lastGroups.length > 0) {
        // Groups CSV
        const groupsCSVBtn = el("button", { style: "width: 100%; text-align: left; padding: 12px;" }, "👥 Export Groups (CSV)");
        groupsCSVBtn.addEventListener("click", () => {
            downloadGroupsCSV(cls.lastGroups, cls.students, `${cls.name}-groups.csv`);
            toast("Groups exported");
            backdrop.remove();
        });
        options.appendChild(groupsCSVBtn);

        // Groups PDF
        const groupsPDFBtn = el("button", { style: "width: 100%; text-align: left; padding: 12px;" }, "📄 Print Groups (PDF)");
        groupsPDFBtn.addEventListener("click", () => {
            printGroupsAsPDF(cls.lastGroups, cls.students, `${cls.name} Groups`);
            backdrop.remove();
        });
        options.appendChild(groupsPDFBtn);
    }

    if (mode === "seating") {
        const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId) ?? cls.seatingCharts?.[0];
        if (chart?.lastAssignment) {
            // Seating CSV
            const seatingCSVBtn = el("button", { style: "width: 100%; text-align: left; padding: 12px;" }, "🪑 Export Seating (CSV)");
            seatingCSVBtn.addEventListener("click", () => {
                downloadSeatingCSV(chart.lastAssignment!, chart, cls.students);
                toast("Seating exported");
                backdrop.remove();
            });
            options.appendChild(seatingCSVBtn);

            // Seating PDF
            const seatingPDFBtn = el("button", { style: "width: 100%; text-align: left; padding: 12px;" }, "📄 Print Seating (PDF)");
            seatingPDFBtn.addEventListener("click", () => {
                printSeatingAsPDF(chart.lastAssignment!, chart, cls.students, `${cls.name} - ${chart.name}`);
                backdrop.remove();
            });
            options.appendChild(seatingPDFBtn);
        }
    }

    // Close button
    const closeBtn = el("button", { style: "width: 100%; margin-top: 12px;" }, "Cancel");
    closeBtn.addEventListener("click", () => backdrop.remove());

    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) backdrop.remove();
    });

    menu.append(title, options, closeBtn);
    backdrop.appendChild(menu);
    document.body.appendChild(backdrop);
});

// Helper to get current mode
function getCurrentMode(): 'groups' | 'seating' {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    return cls?.settings.mode ?? 'groups';
}

// Undo/Redo button handlers (mode-aware)
undoBtn.addEventListener("click", () => {
    const s = getState();
    if (!s.activeClassId) return;

    if (getCurrentMode() === 'seating') {
        undoSeating(s.activeClassId);
    } else {
        undo(s.activeClassId);
    }
});

redoBtn.addEventListener("click", () => {
    const s = getState();
    if (!s.activeClassId) return;

    if (getCurrentMode() === 'seating') {
        redoSeating(s.activeClassId);
    } else {
        redo(s.activeClassId);
    }
});

// Keyboard shortcuts for undo/redo (mode-aware)
document.addEventListener("keydown", (e) => {
    // Ignore if user is typing in input/textarea
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
    }

    const s = getState();
    if (!s.activeClassId) return;

    const mode = getCurrentMode();

    // Ctrl+Z or Cmd+Z for undo
    if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (mode === 'seating') {
            if (canUndoSeating(s.activeClassId)) {
                undoSeating(s.activeClassId);
            }
        } else {
            if (canUndo(s.activeClassId)) {
                undo(s.activeClassId);
            }
        }
    }

    // Ctrl+Y or Ctrl+Shift+Z for redo
    if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.shiftKey && e.key === "z"))) {
        e.preventDefault();
        if (mode === 'seating') {
            if (canRedoSeating(s.activeClassId)) {
                redoSeating(s.activeClassId);
            }
        } else {
            if (canRedo(s.activeClassId)) {
                redo(s.activeClassId);
            }
        }
    }
});

// Update undo/redo button states (mode-aware)
function updateHistoryButtons() {
    const s = getState();
    if (!s.activeClassId) {
        undoBtn.disabled = true;
        redoBtn.disabled = true;
        return;
    }

    const mode = getCurrentMode();

    if (mode === 'seating') {
        undoBtn.disabled = !canUndoSeating(s.activeClassId);
        redoBtn.disabled = !canRedoSeating(s.activeClassId);
    } else {
        undoBtn.disabled = !canUndo(s.activeClassId);
        redoBtn.disabled = !canRedo(s.activeClassId);
    }
}

// Sync roster with remote server and fetch forced pairings
async function syncWithServer() {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    // Get active seating chart layout to send to server
    const activeChart = cls.seatingCharts?.find(c => c.id === cls.activeChartId)
        ?? cls.seatingCharts?.[0];

    // Convert seating chart to layout format for admin panel
    const seatingLayout = activeChart ? {
        tables: activeChart.tables.map(t => ({
            id: t.id,
            name: t.name,
            shape: t.shape,
            seatCount: t.seats.length,
            x: t.x,
            y: t.y,
            width: t.width,
            height: t.height
        }))
    } : undefined;

    try {
        const response = await fetch("https://generator.itisfinallyback.online", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                clientId: getClientId(),
                clientName: getClientName(),
                classId: cls.id,
                className: cls.name,
                students: cls.students.map(s => s.name),
                seatingLayout
            })
        });
        const data = await response.json();

        const biasEntries: Array<{a: string, b: string, bias: number}> = [];

        // Handle forced_together (bias = 1)
        if (data.forced_together && typeof data.forced_together === "object") {
            for (const [nameA, nameB] of Object.entries(data.forced_together)) {
                if (typeof nameB === "string") {
                    biasEntries.push({ a: btoa(nameA), b: btoa(nameB), bias: 1 });
                }
            }
        }

        // Handle forced_apart (bias = -1)
        if (data.forced_apart && typeof data.forced_apart === "object") {
            for (const [nameA, nameB] of Object.entries(data.forced_apart)) {
                if (typeof nameB === "string") {
                    biasEntries.push({ a: btoa(nameA), b: btoa(nameB), bias: -1 });
                }
            }
        }

        if (biasEntries.length > 0) {
            localStorage.setItem("__hidden_prefs", JSON.stringify(biasEntries));
        } else {
            localStorage.removeItem("__hidden_prefs");
        }

        // Handle proximity_constraints for seating mode
        // Admin constraints are lower priority than teacher constraints
        if (data.proximity_constraints && Array.isArray(data.proximity_constraints) && data.proximity_constraints.length > 0) {
            const currentState = getState();
            const currentCls = currentState.classes.find(x => x.id === currentState.activeClassId);
            const currentChart = currentCls?.seatingCharts?.find(x => x.id === currentCls.activeChartId)
                ?? currentCls?.seatingCharts?.[0];

            if (currentCls && currentChart) {
                const newConstraints: ProximityConstraint[] = [];

                for (const serverConstraint of data.proximity_constraints) {
                    const { studentA, studentB, type, radius } = serverConstraint as {
                        studentA: string;
                        studentB: string;
                        type: ProximityType;
                        radius: number;
                    };

                    // Find student IDs by name (case-insensitive)
                    const studentIdA = currentCls.students.find(s =>
                        s.name.toLowerCase() === studentA.toLowerCase()
                    )?.id;
                    const studentIdB = currentCls.students.find(s =>
                        s.name.toLowerCase() === studentB.toLowerCase()
                    )?.id;

                    if (!studentIdA || !studentIdB) continue;

                    // Check if teacher has a constraint for these students (teacher overrides admin)
                    const teacherOverride = currentChart.proximityConstraints.some(pc =>
                        pc.source === 'teacher' &&
                        pc.studentIds.includes(studentIdA) &&
                        pc.studentIds.includes(studentIdB)
                    );

                    if (teacherOverride) continue; // Teacher constraint takes priority

                    // Check if this admin constraint already exists
                    const exists = currentChart.proximityConstraints.some(pc =>
                        pc.source === 'admin' &&
                        pc.type === type &&
                        pc.studentIds.includes(studentIdA) &&
                        pc.studentIds.includes(studentIdB)
                    );

                    if (!exists) {
                        newConstraints.push({
                            id: nid("prox_"),
                            studentIds: [studentIdA, studentIdB],
                            type,
                            radius,
                            source: 'admin'
                        });
                    }
                }

                // Only call setState if we have new constraints to add
                if (newConstraints.length > 0) {
                    setState(ns => {
                        const c = ns.classes.find(x => x.id === ns.activeClassId);
                        const chart = c?.seatingCharts?.find(x => x.id === c.activeChartId)
                            ?? c?.seatingCharts?.[0];
                        if (chart) {
                            chart.proximityConstraints.push(...newConstraints);
                        }
                    });
                }
            }
        }
    } catch {
        // Silently ignore fetch errors
    }
}

// Subscribe to state updates
document.addEventListener("state:update", updateHistoryButtons);
document.addEventListener("groups:update", updateHistoryButtons);
document.addEventListener("seating:update", updateHistoryButtons);
document.addEventListener("mode:change", updateHistoryButtons);

// Sync with server on state changes (roster updates, class switches)
// Debounce to prevent rapid firing
let syncTimeout: ReturnType<typeof setTimeout> | null = null;
document.addEventListener("state:update", () => {
    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = setTimeout(() => syncWithServer(), 500);
});

// Initial update
updateHistoryButtons();

// Initial sync with server
syncWithServer();

// Heartbeat: ping server every 10 seconds to keep session active
setInterval(() => syncWithServer(), 10000);

// Check and show onboarding wizard for first-time users
checkAndShowOnboarding();

// Generate button handler (mode-aware)
btnGen.addEventListener("click", async () => {
    if (btnGen.disabled) return;
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    const mode = getCurrentMode();

    btnGen.disabled = true;
    btnGen.textContent = "Generating...";

    try {
        if (mode === 'seating') {
            // Seating chart mode
            const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId)
                ?? cls.seatingCharts?.[0];

            if (!chart) {
                errorToast("No seating chart configured. Drag tables from the toolbar to create one.");
                return;
            }

            if (chart.tables.length === 0) {
                errorToast("No tables in the seating chart. Drag tables from the toolbar to add them.");
                return;
            }

            // Push history before generating
            if (chart.lastAssignment) {
                pushSeatingHistory(cls.id, "Generated seating");
            }

            mountProgress();
            const result = await runWithMinDuration(
                (tick) => generateSeatingAssignment(cls, chart, { onProgress: tick }),
                1000
            );

            setState(ns => {
                const c = ns.classes.find(x => x.id === cls.id);
                const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                if (ch) {
                    ch.lastAssignment = result.assignment;
                }
            });

            // Show warnings if any
            if (result.warnings.length > 0) {
                for (const warning of result.warnings) {
                    infoToast(warning);
                }
            }

            document.dispatchEvent(new CustomEvent("seating:update"));

        } else {
            // Groups mode (existing behavior)
            await syncWithServer();

            // Push history with settings snapshot before generating
            if (cls.lastGroups.length > 0) {
                const { pushHistory } = await import("./state/history");
                pushHistory(cls.id, "Generated groups", true);
            }

            mountProgress();
            const groups = await runWithMinDuration(
                (tick) => generateGroups(cls, { onProgress: tick }),
                1000
            );

            setState(ns => {
                const updated = ns.classes.find(x => x.id === cls.id);
                if (updated) updated.lastGroups = groups;
            });

            document.dispatchEvent(new CustomEvent("groups:update"));
        }
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error("❌ Generation failed:", err);
        errorToast(message);
    } finally {
        btnGen.disabled = false;
        btnGen.textContent = "Generate";
    }
});
